<template>
  <v-card class="px-3 py-10">
    <mf-funnel-graph
      direction="horizontal"
      gradient-direction="horizontal"
      sub-label-value="percent"
      tooltip-revenue="Faturamento que esses clientes geraram"
      tooltip-percentage="Porcentagem dos clientes que receberam"
      full-width
      :sub-labels="subLabels"
      :values="values"
      :colors="colors"
      :height="300"
      :labels="labels"
      :label-revenues="labelRevenues"
      :label-values="titles"
      :percentage-values="percentageValues"
      :loading="loading"
    >
      <template v-slot:quick-action="{ index }">
        <mf-quick-actions v-if="quickActionsProps[index]" v-bind="quickActionsProps[index]" />
      </template>
    </mf-funnel-graph>
  </v-card>
</template>

<script>
import { QUERY_GENERAL_RESULTS_FUNNEL } from '@/modules/campanhas/resultados-gerais/graphql'
import {
  criteriaGroup,
  campaignReceivedCriteria,
  campaignReceivedAndPurchasedCriteria,
  campaignReceivedAndPurchasedAnyProductCriteria,
  campaignReceivedAndOnlyPurchasedAnyProductCriteria,
} from '@/components/quick-actions/segmentationGroups.js'
import { assertArray, localeString } from '@/config/helpers.js'
import { mapState } from 'vuex'
export default {
  name: 'BuyProfileFunnel',
  props: {
    campaignIds: {
      type: Array,
      default: () => [],
    },
    productIds: {
      type: Array,
      default: () => [],
    },
    loadingCampaigns: {
      type: Boolean,
      default: false,
    },
    channels: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    locale: localeString,
    campaignsChannelsMetrics: {},
  }),
  computed: {
    ...mapState({
      stateFilters: (state) => state.filters,
    }),
    skipQuery() {
      return !this.defaultQueryVariables || !this.defaultQueryVariables.initialDate || !this.defaultQueryVariables.finalDate || !this.campaignIds.length
    },
    loading() {
      return this.$apollo.queries.campaignsChannelsMetrics.loading
    },
    defaultQueryVariables() {
      const { data } = this.stateFilters
      const [initialDate = '', finalDate = ''] = assertArray(data)
      const variables = {
        initialDate,
        finalDate,
        channels: this.channels,
        campaignIds: this.campaignIds,
        productIds: this.productsIds,
      }

      return variables
    },
    labels() {
      return [
        'Clientes receberam',
        'Clientes receberam e compraram',
        'Clientes receberam e compraram produtos divulgados',
        'Clientes receberam e compraram apenas produtos divulgados',
      ]
    },
    subLabels() {
      if (this.skipGetters) return []
      const selectedChannels = this.stateFilters.channels
      let labels = []

      if (selectedChannels.includes('SMS')) labels.push('Receberam SMS')
      if (selectedChannels.includes('EMAIL')) labels.push('Receberam E-mail')
      if (selectedChannels.includes('WHATSAPP')) labels.push('Receberam Whatsapp')
      if (selectedChannels.includes('PUSH')) labels.push('Receberam Push aplicativo')

      return labels
    },
    colors() {
      return [
        ['#209A7A', '#3DD88C'],
        ['#001D3A', '#3793F1'],
        ['#09535C', '#158372'],
        ['#EAF9E8', '#B9EBCF'],
      ]
    },
    skipGetters() {
      return this.loading || !this.campaignsChannelsMetrics || !this.campaignsChannelsMetrics.client_metrics
    },
    values() {
      if (this.skipGetters)
        return [
          [0, 0, 0, 0],
          [0, 0, 0, 0],
          [0, 0, 0, 0],
          [0, 0, 0, 0],
        ]
      const selectedChannels = this.stateFilters.channels

      const sms = this.getChannelMetrics('sms')
      const email = this.getChannelMetrics('email')
      const whatsapp = this.getChannelMetrics('whatsapp')
      const push = this.getChannelMetrics('push')

      let first = []
      let second = []
      let third = []
      let fourth = []

      if (selectedChannels.includes('SMS')) {
        first.push(sms[0])
        second.push(sms[1])
        third.push(sms[2])
        fourth.push(sms[3])
      }
      if (selectedChannels.includes('EMAIL')) {
        first.push(email[0])
        second.push(email[1])
        third.push(email[2])
        fourth.push(email[3])
      }
      if (selectedChannels.includes('WHATSAPP')) {
        first.push(whatsapp[0])
        second.push(whatsapp[1])
        third.push(whatsapp[2])
        fourth.push(whatsapp[3])
      }
      if (selectedChannels.includes('PUSH')) {
        first.push(push[0])
        second.push(push[1])
        third.push(push[2])
        fourth.push(push[3])
      }
      return [first, second, third, fourth]
    },
    labelRevenues() {
      if (this.skipGetters) return [0, 0, 0, 0]
      const metrics = this.campaignsChannelsMetrics.client_metrics.general
      return [
        metrics.received.revenue,
        metrics.received_and_bought.revenue,
        metrics.received_with_products.revenue,
        metrics.received_with_only_products.revenue,
      ]
    },
    titles() {
      if (this.skipGetters) return [0, 0, 0, 0]
      const metrics = this.campaignsChannelsMetrics.client_metrics.general
      return [metrics.received.count, metrics.received_and_bought.count, metrics.received_with_products.count, metrics.received_with_only_products.count]
    },
    percentageValues() {
      const maxValue = Math.max(...this.titles)
      const roundPoint = (number) => Math.round(number * 10) / 10

      return this.titles.map((value) => (value === 0 ? 0 : roundPoint((value * 100) / maxValue)))
    },
    quickActionsProps() {
      return [
        {
          key: `clients-received-campaigns`,
          quickActionGroupId: `clients-received-campaigns`,
          shortDescription: 'Clientes que receberam campanhas',
          longDescription: ({ startDate, endDate }) => {
            return `Clientes que receberam campanhas entre ${startDate} e ${endDate}`
          },
          segmentationGroup: ({ dateCriteriaFilter }) => criteriaGroup([campaignReceivedCriteria(this.campaignIds, null, null, this.channels)]),
        },
        {
          key: `clients-received-campaigns-and-bought`,
          quickActionGroupId: `clients-received-campaigns-and-bought`,
          shortDescription: 'Clientes que receberam campanhas e compraram produtos divulgados',
          longDescription: ({ startDate, endDate }) => {
            return `Clientes que receberam campanhas entre ${startDate} e ${endDate} e compraram produtos divulgados`
          },
          segmentationGroup: ({ dateCriteriaFilter }) => criteriaGroup([campaignReceivedAndPurchasedCriteria(this.campaignIds, null, null, this.channels)]),
        },
        {
          key: `clients-received-campaigns-and-bought-products`,
          quickActionGroupId: `clients-received-campaigns-and-bought-products`,
          shortDescription: 'Clientes que receberam campanhas e compraram apenas produtos divulgados',
          longDescription: ({ startDate, endDate }) => {
            return `Clientes que receberam campanhas entre ${startDate} e ${endDate} e compraram apenas produtos divulgados`
          },
          segmentationGroup: ({ dateCriteriaFilter }) =>
            criteriaGroup([campaignReceivedAndPurchasedAnyProductCriteria(this.campaignIds, null, null, this.channels)]),
        },
        {
          key: `clients-received-campaigns`,
          quickActionGroupId: `clients-received-campaigns-and-bought-only-products`,
          shortDescription: 'Clientes que receberam campanhas-and-bought-only-products',
          longDescription: ({ startDate, endDate }) => {
            return `Clientes que receberam campanhas entre ${startDate} e ${endDate}`
          },
          segmentationGroup: ({ dateCriteriaFilter }) =>
            criteriaGroup([campaignReceivedAndOnlyPurchasedAnyProductCriteria(this.campaignIds, null, null, this.channels)]),
        },
      ]
    },
  },
  apollo: {
    campaignsChannelsMetrics: {
      query: QUERY_GENERAL_RESULTS_FUNNEL,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      update({ campaignsChannelsMetrics }) {
        return campaignsChannelsMetrics
      },
      variables() {
        return this.defaultQueryVariables
      },
      context() {
        return {
          uri: this.$microservicesUrls['campaigns'],
        }
      },
      skip() {
        return this.skipQuery || !this.campaignIds.length
      },
    },
  },
  watch: {
    campaignIds: {
      deep: true,
      handler: function () {
        this.campaignsChannelsMetrics = {}
        if (!this.skipQuery && this.campaignIds.length) {
          const variables = {
            ...this.defaultQueryVariables,
            productIds: this.productIds,
          }
          this.$apollo.queries.campaignsChannelsMetrics.refetch({
            ...variables,
          })
        }
      },
    },
  },
  methods: {
    getChannelMetrics(channel) {
      if (this.skipGetters) return []
      const channelData = this.campaignsChannelsMetrics?.client_metrics?.channels[channel]
      return [
        channelData.received.count,
        channelData.received_and_bought.count,
        channelData.received_with_products.count,
        channelData.received_with_only_products.count,
      ]
    },
  },
}
</script>

<style lang="scss" scoped></style>
